import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, RotateCcw, Ban } from "lucide-react";

export function ModerationQueue() {
  const [posts, setPosts] = useState([]);
  const load = async () => {
    const r = await api.get("/clinician/moderation");
    setPosts(r.data.posts);
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    await api.post(`/clinician/moderation/${id}`, { action });
    toast.success(action === "reinstate" ? "Note reinstated." : "Note removed.");
    load();
  };

  return (
    <div className="warm-card p-6" data-testid="moderation-queue">
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert className="w-4 h-4 text-amber-700" />
        <h3 className="font-display font-bold text-stone-900">Community moderation</h3>
        <span className="ml-auto font-mono text-xs text-stone-500">{posts.length} flagged</span>
      </div>
      <p className="text-xs text-stone-500 mb-4">Notes flagged or removed that involve the people you support. Reinstate if the flag was a mistake, or confirm removal.</p>
      {posts.length === 0 ? (
        <div className="text-sm text-stone-500">Nothing flagged. The community is being kind.</div>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto">
          {posts.map((p) => (
            <li key={p.id} data-testid={`mod-post-${p.id}`} className="rounded-2xl border border-stone-100 p-3 text-sm">
              <div className="flex items-center justify-between gap-2 text-[10px] font-bold uppercase tracking-widest">
                <span className={p.kind === "support" ? "text-emerald-800" : "text-amber-800"}>
                  {p.kind} · {p.from_name} → {p.to_name} · {p.category}
                </span>
                <span className={`px-2 py-0.5 rounded-full ${p.status === "removed" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`} data-testid={`mod-status-${p.id}`}>
                  {p.status === "removed" ? `removed (${p.removed_reason || "flagged"})` : `${p.flag_count} flag${p.flag_count === 1 ? "" : "s"}`}
                </span>
              </div>
              <p className="text-stone-800 mt-1">{p.text}</p>
              {p.flag_reasons?.length > 0 && (
                <div className="text-[11px] text-stone-500 mt-1">Reasons: {p.flag_reasons.map((f) => f.reason.replace("_", " ")).join(", ")}</div>
              )}
              <div className="flex gap-2 mt-2">
                {p.status === "removed" ? (
                  <button data-testid={`mod-reinstate-${p.id}`} onClick={() => act(p.id, "reinstate")} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200">
                    <RotateCcw className="w-3 h-3" /> Reinstate
                  </button>
                ) : (
                  <>
                    <button data-testid={`mod-remove-${p.id}`} onClick={() => act(p.id, "remove")} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-red-100 text-red-800 hover:bg-red-200">
                      <Ban className="w-3 h-3" /> Remove
                    </button>
                    <button data-testid={`mod-clear-${p.id}`} onClick={() => act(p.id, "reinstate")} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-stone-100 text-stone-700 hover:bg-stone-200">
                      Clear flags
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
